import { useState, useRef, useEffect } from 'react';
import { useInquiry } from './InquiryProvider';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/tailwind';
import { TabKey } from '../../_consts/inquiryConstants';

interface TabCount {
  key: string;
  label: string;
  count: number;
}

interface ContentsTabProps {
  tabListWithCounts: TabCount[];
}

const ContentsTab = ({ tabListWithCounts }: ContentsTabProps) => {
  const { currentTab, setCurrentTab } = useInquiry();

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tabBarRef = useRef<HTMLDivElement | null>(null);

  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
    bottom: number;
  } | null>(null);

  // ✅ 클릭(탭 변경) 기준으로 인디케이터 위치 업데이트
  useEffect(() => {
    const key = currentTab;
    if (key && tabRefs.current[key] && tabBarRef.current) {
      const node = tabRefs.current[key]!;
      const containerRect = tabBarRef.current.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();

      setIndicator({
        left: nodeRect.left - containerRect.left,
        width: nodeRect.width,
        bottom: containerRect.bottom - nodeRect.bottom,
      });
    }
  }, [currentTab, tabListWithCounts]);

  return (
    <div ref={tabBarRef} className="relative flex flex-wrap gap-x-[20px] gap-y-[10px]">
      <AnimatePresence>
        {indicator && (
          <motion.span
            layoutId="inquiryTabIndicator"
            className="absolute bottom-0 h-[2px] rounded-full bg-gr-800"
            style={{
              left: indicator.left,
              width: indicator.width,
            }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { type: 'spring', stiffness: 400, damping: 30 },
            }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      {/* 탭 버튼들 */}
      {tabListWithCounts.map((tab, idx) => (
        <TabButton
          key={tab.key}
          tab={tab}
          idx={idx}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          tabRefs={tabRefs}
        />
      ))}
    </div>
  );
};

export default ContentsTab;

interface TabButtonProps {
  tab: TabCount;
  idx: number;
  currentTab: TabKey;
  setCurrentTab: (key: TabKey) => void;
  tabRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
}

export const TabButton = ({ tab, idx, currentTab, setCurrentTab, tabRefs }: TabButtonProps) => {
  const minWidthClass = idx === 0 ? 'min-w-[66px]' : idx === 1 ? 'min-w-[68px]' : '';

  return (
    <button
      ref={(el) => (tabRefs.current[tab.key] = el)}
      className={cn(
        'group relative border-b-2 pb-[18px] transition-colors Body1S16',
        minWidthClass,
        currentTab === tab.key
          ? 'border-transparent text-gr-900' // border는 indicator가 덮어씌움
          : 'border-transparent text-gr-500 hover:text-gr-700',
      )}
      onClick={() => setCurrentTab(tab.key as TabKey)}
    >
      <div className="flex flex-row items-center gap-2">
        <span className="whitespace-nowrap">{tab.label}</span>
        <span
          className={cn(
            'text-[0.9em]',
            currentTab === tab.key ? 'text-gr-900' : 'text-gr-400 opacity-60',
            'transition-colors duration-300 group-hover:text-gr-700',
          )}
        >
          {tab.count}
        </span>
      </div>
    </button>
  );
};
